import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function GestionRevendeurs() {
  // ============================================
  // STATES
  // ============================================
  const [revendeurs, setRevendeurs] = useState([]);
  const [typesRevendeurs, setTypesRevendeurs] = useState([]);
  const [selectedRevendeur, setSelectedRevendeur] = useState(null);
  const [mouvements, setMouvements] = useState([]);
  const [notesCommerciales, setNotesCommerciales] = useState([]);
  const [rappelsEnRetard, setRappelsEnRetard] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Modales
  const [showModalRevendeur, setShowModalRevendeur] = useState(false);
  const [showModalCredit, setShowModalCredit] = useState(false);
  const [showModalDebit, setShowModalDebit] = useState(false);
  const [showModalNote, setShowModalNote] = useState(false);
  const [showModalType, setShowModalType] = useState(false);
  const [editingRevendeur, setEditingRevendeur] = useState(null);

  // Formulaire revendeur
  const [formRevendeur, setFormRevendeur] = useState({
    nom_boutique: '', prenom_contact: '', nom_contact: '',
    type_revendeur_id: '', telephone: '', email: '',
    siret: '', tva_intracommunautaire: '', site_internet: '',
    adresse: '', code_postal: '', ville: '', code_pays: 'FR'
  });

  // Formulaire crédit
  const [formCredit, setFormCredit] = useState({
    montant: '', bonus: '', reference: '', date_document: new Date().toISOString().split('T')[0], notes: ''
  });

  // Formulaire débit
  const [formDebit, setFormDebit] = useState({
    montant: '', reference: '', date_document: new Date().toISOString().split('T')[0], notes: ''
  });

  // Formulaire note commerciale
  const [formNote, setFormNote] = useState({
    date_contact: new Date().toISOString().split('T')[0], contenu: '', date_rappel: ''
  });

  // Formulaire type revendeur
  const [newTypeName, setNewTypeName] = useState('');

  // Envoi d'email
  const [envoyerEmail, setEnvoyerEmail] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  // Documents PDF
  const [pdfFile, setPdfFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showModalDocument, setShowModalDocument] = useState(false);
  const [formDocType, setFormDocType] = useState('autre');

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================
  useEffect(() => {
    loadRevendeurs();
    loadTypesRevendeurs();
    loadRappelsEnRetard();
  }, []);

  const loadRevendeurs = async () => {
    const { data, error } = await supabase
      .from('revendeurs')
      .select('*, types_revendeurs(nom)')
      .order('nom_boutique', { ascending: true });
    if (error) {
      console.error('Erreur chargement revendeurs:', error);
    } else {
      setRevendeurs(data || []);
    }
  };

  const loadTypesRevendeurs = async () => {
    const { data, error } = await supabase
      .from('types_revendeurs')
      .select('*')
      .order('nom', { ascending: true });
    if (!error) setTypesRevendeurs(data || []);
  };

  const loadRappelsEnRetard = async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('notes_commerciales')
      .select('*, revendeurs(nom_boutique)')
      .eq('rappel_effectue', false)
      .lte('date_rappel', today)
      .order('date_rappel', { ascending: true });
    if (!error) setRappelsEnRetard(data || []);
  };

  const loadMouvements = async (revendeurId) => {
    const { data, error } = await supabase
      .from('mouvements_credit')
      .select('*')
      .eq('revendeur_id', revendeurId)
      .order('created_at', { ascending: false });
    if (!error) setMouvements(data || []);
  };

  const loadNotes = async (revendeurId) => {
    const { data, error } = await supabase
      .from('notes_commerciales')
      .select('*')
      .eq('revendeur_id', revendeurId)
      .order('date_contact', { ascending: false });
    if (!error) setNotesCommerciales(data || []);
  };

  const loadDocuments = async (revendeurId) => {
    const { data, error } = await supabase
      .from('documents_revendeurs')
      .select('*')
      .eq('revendeur_id', revendeurId)
      .order('created_at', { ascending: false });
    if (!error) setDocuments(data || []);
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Supprimer le document "${doc.nom_fichier}" ?`)) return;
    // Supprimer du storage
    const pathParts = doc.url.split('/documents-revendeurs/');
    if (pathParts.length > 1) {
      await supabase.storage.from('documents-revendeurs').remove([pathParts[1]]);
    }
    // Supprimer de la table
    const { error } = await supabase.from('documents_revendeurs').delete().eq('id', doc.id);
    if (!error) {
      await loadDocuments(selectedRevendeur.id);
    }
  };

  const openModalDocument = () => {
    setPdfFile(null);
    setFormDocType('autre');
    setShowModalDocument(true);
  };

  const handleAjouterDocumentManuel = async (e) => {
    e.preventDefault();
    if (!pdfFile) {
      alert('Veuillez sélectionner un fichier PDF');
      return;
    }

    try {
      const filePath = `${selectedRevendeur.id}/manuel_${Date.now()}_${pdfFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('documents-revendeurs')
        .upload(filePath, pdfFile, { upsert: true });
      if (uploadError) {
        alert('Erreur lors de l\'upload du fichier');
        console.error('Erreur upload PDF:', uploadError);
        return;
      }

      const { data: urlData } = supabase.storage.from('documents-revendeurs').getPublicUrl(filePath);
      const { error: insertError } = await supabase.from('documents_revendeurs').insert({
        revendeur_id: selectedRevendeur.id,
        mouvement_id: null,
        nom_fichier: pdfFile.name,
        url: urlData.publicUrl,
        type_document: formDocType,
        taille_octets: pdfFile.size
      });

      if (insertError) {
        alert('Erreur lors de l\'enregistrement');
        console.error(insertError);
        return;
      }

      setShowModalDocument(false);
      setPdfFile(null);
      await loadDocuments(selectedRevendeur.id);
      alert(`✅ Document "${pdfFile.name}" ajouté avec succès !`);
    } catch (err) {
      console.error('Erreur ajout document:', err);
      alert('Erreur inattendue');
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // enlever le préfixe data:...;base64,
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  // ============================================
  // SÉLECTION D'UN REVENDEUR (fiche détail)
  // ============================================
  const handleSelectRevendeur = async (revendeur) => {
    setSelectedRevendeur(revendeur);
    await loadMouvements(revendeur.id);
    await loadNotes(revendeur.id);
    await loadDocuments(revendeur.id);
  };

  const handleRetourListe = () => {
    setSelectedRevendeur(null);
    setMouvements([]);
    setNotesCommerciales([]);
    setDocuments([]);
  };

  // ============================================
  // CRUD REVENDEUR
  // ============================================
  const openModalRevendeur = (revendeur = null) => {
    if (revendeur) {
      setEditingRevendeur(revendeur);
      setFormRevendeur({
        nom_boutique: revendeur.nom_boutique || '',
        prenom_contact: revendeur.prenom_contact || '',
        nom_contact: revendeur.nom_contact || '',
        type_revendeur_id: revendeur.type_revendeur_id || '',
        telephone: revendeur.telephone || '',
        email: revendeur.email || '',
        siret: revendeur.siret || '',
        tva_intracommunautaire: revendeur.tva_intracommunautaire || '',
        site_internet: revendeur.site_internet || '',
        adresse: revendeur.adresse || '',
        code_postal: revendeur.code_postal || '',
        ville: revendeur.ville || '',
        code_pays: revendeur.code_pays || 'FR'
      });
    } else {
      setEditingRevendeur(null);
      setFormRevendeur({
        nom_boutique: '', prenom_contact: '', nom_contact: '',
        type_revendeur_id: '', telephone: '', email: '',
        siret: '', tva_intracommunautaire: '', site_internet: '',
        adresse: '', code_postal: '', ville: '', code_pays: 'FR'
      });
    }
    setShowModalRevendeur(true);
  };

  const handleSaveRevendeur = async (e) => {
    e.preventDefault();
    if (!formRevendeur.nom_boutique.trim()) {
      alert('Le nom de la boutique est obligatoire');
      return;
    }

    const dataToSave = {
      ...formRevendeur,
      type_revendeur_id: formRevendeur.type_revendeur_id || null,
      updated_at: new Date().toISOString()
    };

    if (editingRevendeur) {
      const { error } = await supabase
        .from('revendeurs')
        .update(dataToSave)
        .eq('id', editingRevendeur.id);
      if (error) {
        alert('Erreur lors de la modification');
        console.error(error);
        return;
      }
    } else {
      const { error } = await supabase
        .from('revendeurs')
        .insert(dataToSave);
      if (error) {
        alert('Erreur lors de la création');
        console.error(error);
        return;
      }
    }

    setShowModalRevendeur(false);
    await loadRevendeurs();
    // Si on était sur la fiche, recharger
    if (selectedRevendeur && editingRevendeur) {
      const { data } = await supabase
        .from('revendeurs')
        .select('*, types_revendeurs(nom)')
        .eq('id', editingRevendeur.id)
        .single();
      if (data) setSelectedRevendeur(data);
    }
  };

  const handleDeleteRevendeur = async (id) => {
    if (!window.confirm('⚠️ Supprimer ce revendeur et tout son historique ?\n\nCette action est irréversible.')) return;
    const { error } = await supabase.from('revendeurs').delete().eq('id', id);
    if (error) {
      alert('Erreur lors de la suppression');
      return;
    }
    if (selectedRevendeur?.id === id) handleRetourListe();
    await loadRevendeurs();
  };

  // ============================================
  // ENVOI D'EMAIL
  // ============================================
  const genererEmailHTML = (revendeur, type, montant, reference, dateDoc, nouveauSolde, dateExpiration, bonusMontant = 0) => {
    const isCredit = type === 'credit';
    const couleur = isCredit ? '#28a745' : '#e67e22';
    const icone = isCredit ? '💳' : '📦';
    const titre = isCredit ? 'Rechargement de crédit' : 'Nouvelle commande';
    const labelRef = isCredit ? 'N° de facture' : 'N° bon de commande';
    const dateFormatee = new Date(dateDoc).toLocaleDateString('fr-FR');
    const soldeNegatif = nouveauSolde < 0;
    const hasBonus = isCredit && bonusMontant > 0;
    const montantBase = hasBonus ? montant : montant;
    const creditTotal = hasBonus ? montant + bonusMontant : montant;

    return `
    <!DOCTYPE html>
    <html>
    <head><meta charset="utf-8"></head>
    <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #333;">
      
      <div style="text-align: center; padding: 30px 0 20px;">
        <h1 style="margin: 0; font-size: 24px; color: #1a1a2e;">ElevenMX</h1>
        <p style="margin: 5px 0 0; color: #6c757d; font-size: 14px;">Gestion des crédits revendeurs</p>
      </div>

      <div style="background: ${couleur}15; border-left: 4px solid ${couleur}; padding: 20px; border-radius: 0 12px 12px 0; margin-bottom: 24px;">
        <h2 style="margin: 0 0 5px; color: ${couleur}; font-size: 20px;">${icone} ${titre}</h2>
        <p style="margin: 0; color: #6c757d; font-size: 14px;">${dateFormatee}</p>
      </div>

      <div style="background: #f8f9fa; border-radius: 12px; padding: 20px; margin-bottom: 24px;">
        <h3 style="margin: 0 0 16px; color: #495057; font-size: 16px;">Détails de l'opération</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #6c757d;">Boutique</td>
            <td style="padding: 8px 0; text-align: right; font-weight: 600;">${revendeur.nom_boutique}</td>
          </tr>
          ${reference ? `<tr>
            <td style="padding: 8px 0; color: #6c757d;">${labelRef}</td>
            <td style="padding: 8px 0; text-align: right; font-weight: 600;">${reference}</td>
          </tr>` : ''}
          ${isCredit ? `<tr>
            <td style="padding: 8px 0; color: #6c757d;">Montant crédité</td>
            <td style="padding: 8px 0; text-align: right; font-weight: 700; font-size: 16px; color: ${couleur};">
              +${montantBase.toFixed(2)} €
            </td>
          </tr>` : ''}
          ${hasBonus ? `<tr>
            <td style="padding: 8px 0; color: #6c757d;">🎁 Bonus offert</td>
            <td style="padding: 8px 0; text-align: right; font-weight: 700; font-size: 16px; color: #6f42c1;">
              +${bonusMontant.toFixed(2)} €
            </td>
          </tr>
          <tr style="border-top: 2px solid #dee2e6;">
            <td style="padding: 10px 0 8px; color: #212529; font-weight: 600;">Crédit total</td>
            <td style="padding: 10px 0 8px; text-align: right; font-weight: 800; font-size: 18px; color: ${couleur};">
              +${creditTotal.toFixed(2)} €
            </td>
          </tr>` : ''}
          ${!isCredit ? `<tr>
            <td style="padding: 8px 0; color: #6c757d;">Montant débité</td>
            <td style="padding: 8px 0; text-align: right; font-weight: 700; font-size: 18px; color: ${couleur};">
              -${montant.toFixed(2)} €
            </td>
          </tr>` : ''}
        </table>
      </div>

      <div style="background: ${soldeNegatif ? '#fde8e8' : '#e8f5e9'}; border-radius: 12px; padding: 20px; margin-bottom: 24px; text-align: center;">
        <p style="margin: 0 0 8px; color: #6c757d; font-size: 14px;">Votre solde actuel</p>
        <p style="margin: 0; font-size: 32px; font-weight: 800; color: ${soldeNegatif ? '#dc3545' : '#28a745'};">
          ${nouveauSolde.toFixed(2)} €
        </p>
        ${dateExpiration ? `<p style="margin: 8px 0 0; color: #6c757d; font-size: 12px;">
          Expiration du crédit : ${new Date(dateExpiration).toLocaleDateString('fr-FR')}
        </p>` : ''}
      </div>

      <hr style="border: none; border-top: 1px solid #e9ecef; margin: 30px 0;">
      
      <p style="text-align: center; color: #adb5bd; font-size: 12px; margin: 0;">
        Ce message a été envoyé automatiquement par ElevenMX.<br>
        Pour toute question, contactez-nous à contact@elevenmx.com
      </p>
    </body>
    </html>`;
  };

  const envoyerEmailRecap = async (revendeur, type, montant, reference, dateDoc, nouveauSolde, dateExpiration, bonusMontant = 0, pdfAttachment = null) => {
    if (!revendeur.email) {
      console.warn('Pas d\'email pour ce revendeur');
      return false;
    }

    const isCredit = type === 'credit';
    const creditTotal = isCredit && bonusMontant > 0 ? montant + bonusMontant : montant;
    const subject = isCredit
      ? `💳 Rechargement de ${creditTotal.toFixed(2)}€ - ${revendeur.nom_boutique}`
      : `📦 Commande de ${montant.toFixed(2)}€ - ${revendeur.nom_boutique}`;

    const html = genererEmailHTML(revendeur, type, montant, reference, dateDoc, nouveauSolde, dateExpiration, bonusMontant);

    try {
      setEnvoiEnCours(true);
      const emailBody = {
        to: revendeur.email,
        subject,
        html,
        revendeurNom: revendeur.nom_boutique,
      };

      // Ajouter le PDF en pièce jointe si présent
      if (pdfAttachment) {
        emailBody.attachments = [{
          filename: pdfAttachment.filename,
          content: pdfAttachment.content,
          contentType: 'application/pdf'
        }];
      }

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailBody),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur envoi');

      // Marquer l'email comme envoyé dans le dernier mouvement
      const { data: dernierMvt } = await supabase
        .from('mouvements_credit')
        .select('id')
        .eq('revendeur_id', revendeur.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (dernierMvt) {
        await supabase
          .from('mouvements_credit')
          .update({ email_envoye: true })
          .eq('id', dernierMvt.id);
      }

      return true;
    } catch (error) {
      console.error('Erreur envoi email:', error);
      return false;
    } finally {
      setEnvoiEnCours(false);
    }
  };

  // ============================================
  // CRÉDIT (rechargement)
  // ============================================
  const openModalCredit = () => {
    setFormCredit({
      montant: '', bonus: '', reference: '', date_document: new Date().toISOString().split('T')[0], notes: ''
    });
    setEnvoyerEmail(!!selectedRevendeur?.email);
    setPdfFile(null);
    setShowModalCredit(true);
  };

  const handleAjouterCredit = async (e) => {
    e.preventDefault();
    const montant = parseFloat(formCredit.montant);
    const bonus = parseFloat(formCredit.bonus) || 0;
    if (!montant || montant <= 0) {
      alert('Montant invalide');
      return;
    }
    if (bonus < 0) {
      alert('Le bonus ne peut pas être négatif');
      return;
    }

    const creditTotal = montant + bonus;

    // 1. Insérer le mouvement avec ventilation base/bonus
    const { data: mvtData, error: mvtError } = await supabase.from('mouvements_credit').insert({
      revendeur_id: selectedRevendeur.id,
      type: 'credit',
      montant: creditTotal,
      bonus_montant: bonus,
      reference: formCredit.reference,
      date_document: formCredit.date_document,
      notes: formCredit.notes
    }).select('id').single();
    if (mvtError) { alert('Erreur'); console.error(mvtError); return; }

    // 2. Mettre à jour le solde (base + bonus) + date expiration (1 an)
    const nouveauSolde = (selectedRevendeur.credit_actuel || 0) + creditTotal;
    const dateExpiration = new Date();
    dateExpiration.setFullYear(dateExpiration.getFullYear() + 1);

    const { error: updError } = await supabase
      .from('revendeurs')
      .update({
        credit_actuel: nouveauSolde,
        date_expiration_credit: dateExpiration.toISOString().split('T')[0],
        updated_at: new Date().toISOString()
      })
      .eq('id', selectedRevendeur.id);
    if (updError) { alert('Erreur mise à jour solde'); console.error(updError); return; }

    // 2b. Upload PDF si présent
    let pdfBase64 = null;
    if (pdfFile) {
      try {
        pdfBase64 = await fileToBase64(pdfFile);
        const filePath = `${selectedRevendeur.id}/${mvtData.id}_${pdfFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('documents-revendeurs')
          .upload(filePath, pdfFile, { upsert: true });
        if (uploadError) {
          console.error('Erreur upload PDF:', uploadError);
        } else {
          const { data: urlData } = supabase.storage.from('documents-revendeurs').getPublicUrl(filePath);
          await supabase.from('documents_revendeurs').insert({
            revendeur_id: selectedRevendeur.id,
            mouvement_id: mvtData.id,
            nom_fichier: pdfFile.name,
            url: urlData.publicUrl,
            type_document: 'facture',
            taille_octets: pdfFile.size
          });
        }
      } catch (err) {
        console.error('Erreur traitement PDF:', err);
      }
    }

    // Rafraîchir
    setShowModalCredit(false);
    setPdfFile(null);
    const { data: rev } = await supabase
      .from('revendeurs')
      .select('*, types_revendeurs(nom)')
      .eq('id', selectedRevendeur.id)
      .single();
    if (rev) setSelectedRevendeur(rev);
    await loadMouvements(selectedRevendeur.id);
    await loadDocuments(selectedRevendeur.id);
    await loadRevendeurs();

    // 3. Envoi email si demandé
    let emailMsg = '';
    if (envoyerEmail && selectedRevendeur.email) {
      const emailOk = await envoyerEmailRecap(
        selectedRevendeur, 'credit', montant, formCredit.reference,
        formCredit.date_document, nouveauSolde, dateExpiration.toISOString().split('T')[0],
        bonus, pdfFile && pdfBase64 ? { filename: pdfFile.name, content: pdfBase64 } : null
      );
      emailMsg = emailOk 
        ? `\n📧 Récapitulatif envoyé à ${selectedRevendeur.email}`
        : `\n⚠️ Erreur lors de l'envoi de l'email`;
    }

    const bonusMsg = bonus > 0 ? `\nDont bonus offert : ${bonus.toFixed(2)}€` : '';
    const pdfMsg = pdfFile ? `\n📄 Document "${pdfFile.name}" sauvegardé` : '';
    alert(`✅ Crédit de ${creditTotal.toFixed(2)}€ ajouté avec succès !${bonusMsg}\nNouveau solde : ${nouveauSolde.toFixed(2)}€\nExpiration : ${dateExpiration.toLocaleDateString('fr-FR')}${pdfMsg}${emailMsg}`);
  };

  // ============================================
  // DÉBIT (commande)
  // ============================================
  const openModalDebit = () => {
    setFormDebit({
      montant: '', reference: '', date_document: new Date().toISOString().split('T')[0], notes: ''
    });
    setEnvoyerEmail(!!selectedRevendeur?.email);
    setPdfFile(null);
    setShowModalDebit(true);
  };

  const handleAjouterDebit = async (e) => {
    e.preventDefault();
    const montant = parseFloat(formDebit.montant);
    if (!montant || montant <= 0) {
      alert('Montant invalide');
      return;
    }

    const nouveauSolde = (selectedRevendeur.credit_actuel || 0) - montant;
    if (nouveauSolde < 0) {
      if (!window.confirm(`⚠️ Attention : le solde sera négatif (${nouveauSolde.toFixed(2)}€).\n\nContinuer quand même ?`)) return;
    }

    // 1. Insérer le mouvement
    const { data: mvtData, error: mvtError } = await supabase.from('mouvements_credit').insert({
      revendeur_id: selectedRevendeur.id,
      type: 'debit',
      montant: montant,
      reference: formDebit.reference,
      date_document: formDebit.date_document,
      notes: formDebit.notes
    }).select('id').single();
    if (mvtError) { alert('Erreur'); console.error(mvtError); return; }

    // 2. Mettre à jour le solde
    const { error: updError } = await supabase
      .from('revendeurs')
      .update({
        credit_actuel: nouveauSolde,
        updated_at: new Date().toISOString()
      })
      .eq('id', selectedRevendeur.id);
    if (updError) { alert('Erreur mise à jour solde'); console.error(updError); return; }

    // 2b. Upload PDF si présent
    let pdfBase64 = null;
    if (pdfFile) {
      try {
        pdfBase64 = await fileToBase64(pdfFile);
        const filePath = `${selectedRevendeur.id}/${mvtData.id}_${pdfFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('documents-revendeurs')
          .upload(filePath, pdfFile, { upsert: true });
        if (uploadError) {
          console.error('Erreur upload PDF:', uploadError);
        } else {
          const { data: urlData } = supabase.storage.from('documents-revendeurs').getPublicUrl(filePath);
          await supabase.from('documents_revendeurs').insert({
            revendeur_id: selectedRevendeur.id,
            mouvement_id: mvtData.id,
            nom_fichier: pdfFile.name,
            url: urlData.publicUrl,
            type_document: 'bon_commande',
            taille_octets: pdfFile.size
          });
        }
      } catch (err) {
        console.error('Erreur traitement PDF:', err);
      }
    }

    // Rafraîchir
    setShowModalDebit(false);
    setPdfFile(null);
    const { data: rev } = await supabase
      .from('revendeurs')
      .select('*, types_revendeurs(nom)')
      .eq('id', selectedRevendeur.id)
      .single();
    if (rev) setSelectedRevendeur(rev);
    await loadMouvements(selectedRevendeur.id);
    await loadDocuments(selectedRevendeur.id);
    await loadRevendeurs();

    // 3. Envoi email si demandé
    let emailMsg = '';
    if (envoyerEmail && selectedRevendeur.email) {
      const emailOk = await envoyerEmailRecap(
        selectedRevendeur, 'debit', montant, formDebit.reference,
        formDebit.date_document, nouveauSolde, selectedRevendeur.date_expiration_credit,
        0, pdfFile && pdfBase64 ? { filename: pdfFile.name, content: pdfBase64 } : null
      );
      emailMsg = emailOk 
        ? `\n📧 Récapitulatif envoyé à ${selectedRevendeur.email}`
        : `\n⚠️ Erreur lors de l'envoi de l'email`;
    }

    const pdfMsg = pdfFile ? `\n📄 Document "${pdfFile.name}" sauvegardé` : '';
    alert(`✅ Débit de ${montant.toFixed(2)}€ enregistré !\nNouveau solde : ${nouveauSolde.toFixed(2)}€${pdfMsg}${emailMsg}`);
  };

  // ============================================
  // NOTES COMMERCIALES
  // ============================================
  const openModalNote = () => {
    setFormNote({
      date_contact: new Date().toISOString().split('T')[0], contenu: '', date_rappel: ''
    });
    setShowModalNote(true);
  };

  const handleAjouterNote = async (e) => {
    e.preventDefault();
    if (!formNote.contenu.trim()) {
      alert('Le contenu de la note est obligatoire');
      return;
    }

    const { error } = await supabase.from('notes_commerciales').insert({
      revendeur_id: selectedRevendeur.id,
      date_contact: formNote.date_contact,
      contenu: formNote.contenu,
      date_rappel: formNote.date_rappel || null
    });
    if (error) { alert('Erreur'); console.error(error); return; }

    setShowModalNote(false);
    await loadNotes(selectedRevendeur.id);
    await loadRappelsEnRetard();
    alert('✅ Note commerciale ajoutée !');
  };

  const handleToggleRappel = async (note) => {
    const { error } = await supabase
      .from('notes_commerciales')
      .update({ rappel_effectue: !note.rappel_effectue })
      .eq('id', note.id);
    if (!error) {
      await loadNotes(selectedRevendeur.id);
      await loadRappelsEnRetard();
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    const { error } = await supabase.from('notes_commerciales').delete().eq('id', noteId);
    if (!error) {
      await loadNotes(selectedRevendeur.id);
      await loadRappelsEnRetard();
    }
  };

  // ============================================
  // TYPES REVENDEURS
  // ============================================
  const handleAjouterType = async () => {
    if (!newTypeName.trim()) return;
    const { error } = await supabase.from('types_revendeurs').insert({ nom: newTypeName.trim() });
    if (error) {
      alert('Erreur (nom déjà existant ?)');
      return;
    }
    setNewTypeName('');
    await loadTypesRevendeurs();
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Supprimer ce type ?')) return;
    const { error } = await supabase.from('types_revendeurs').delete().eq('id', id);
    if (!error) await loadTypesRevendeurs();
  };

  // ============================================
  // HELPERS
  // ============================================
  const getCreditBadge = (revendeur) => {
    const credit = revendeur.credit_actuel || 0;
    const expiration = revendeur.date_expiration_credit;
    const now = new Date();
    const expDate = expiration ? new Date(expiration) : null;
    const joursRestants = expDate ? Math.ceil((expDate - now) / (1000 * 60 * 60 * 24)) : null;

    // Vérifier expiration
    if (expDate && expDate < now) {
      return { color: '#6c757d', bg: '#f8f9fa', label: 'Expiré', icon: '⚫' };
    }
    if (joursRestants !== null && joursRestants <= 30) {
      return { color: '#e67e22', bg: '#fef3e2', label: `Expire dans ${joursRestants}j`, icon: '🟠' };
    }
    if (credit > 1000) return { color: '#28a745', bg: '#e8f5e9', label: `${credit.toFixed(2)}€`, icon: '🟢' };
    if (credit >= 100) return { color: '#e67e22', bg: '#fef3e2', label: `${credit.toFixed(2)}€`, icon: '🟠' };
    if (credit > 0) return { color: '#dc3545', bg: '#fde8e8', label: `${credit.toFixed(2)}€`, icon: '🔴' };
    return { color: '#6c757d', bg: '#f8f9fa', label: '0,00€', icon: '⚪' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const getDernierMouvement = (revendeurId) => {
    // On le calcule à partir de la liste globale chargée
    // Pour la liste, on affiche la dernière date de mouvement depuis la vue
    return null; // Sera calculé lors du chargement
  };

  const revendeursFiltres = revendeurs.filter(r => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      r.nom_boutique?.toLowerCase().includes(s) ||
      r.nom_contact?.toLowerCase().includes(s) ||
      r.prenom_contact?.toLowerCase().includes(s) ||
      r.email?.toLowerCase().includes(s) ||
      r.ville?.toLowerCase().includes(s)
    );
  });

  // Stats pour la fiche
  const totalCredite = mouvements.filter(m => m.type === 'credit').reduce((sum, m) => sum + parseFloat(m.montant) - parseFloat(m.bonus_montant || 0), 0);
  const totalBonus = mouvements.filter(m => m.type === 'credit').reduce((sum, m) => sum + parseFloat(m.bonus_montant || 0), 0);
  const totalCrediteCumule = totalCredite + totalBonus;
  const totalDebite = mouvements.filter(m => m.type === 'debit').reduce((sum, m) => sum + parseFloat(m.montant), 0);
  const nbMouvementsMois = mouvements.filter(m => {
    const d = new Date(m.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  // ============================================
  // RENDER
  // ============================================

  // ---- STYLE COMMUN ----
  const inputStyle = {
    width: '100%', padding: '10px 12px', backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px',
    boxSizing: 'border-box'
  };
  const labelStyle = {
    display: 'block', color: '#495057', marginBottom: '6px', fontSize: '13px', fontWeight: '600'
  };
  const btnPrimary = {
    padding: '12px 24px', backgroundColor: '#1aabf9', color: 'white',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600'
  };
  const btnDanger = {
    padding: '10px 20px', backgroundColor: '#dc3545', color: 'white',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600'
  };
  const btnSecondary = {
    padding: '12px 24px', backgroundColor: '#6c757d', color: 'white',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600'
  };
  const cardStyle = {
    backgroundColor: 'white', borderRadius: '12px', padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flex: 1, textAlign: 'center'
  };
  const modalOverlay = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000
  };
  const modalContent = {
    backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
    width: '560px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  };

  // ==============================================
  // VUE FICHE REVENDEUR
  // ==============================================
  if (selectedRevendeur) {
    const badge = getCreditBadge(selectedRevendeur);
    const dernierContact = notesCommerciales.length > 0 ? notesCommerciales[0].date_contact : null;
    const prochainRappel = notesCommerciales.find(n => !n.rappel_effectue && n.date_rappel);

    return (
      <div>
        {/* Header fiche */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={handleRetourListe} style={{ ...btnSecondary, padding: '10px 16px' }}>
              ← Retour
            </button>
            <h2 style={{ margin: 0, fontSize: '24px', color: '#212529' }}>
              🏪 {selectedRevendeur.nom_boutique}
            </h2>
            <span style={{
              padding: '4px 12px', borderRadius: '20px', fontSize: '12px',
              backgroundColor: selectedRevendeur.actif ? '#e8f5e9' : '#fde8e8',
              color: selectedRevendeur.actif ? '#28a745' : '#dc3545', fontWeight: '600'
            }}>
              {selectedRevendeur.actif ? 'Actif' : 'Inactif'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => openModalRevendeur(selectedRevendeur)} style={btnPrimary}>
              ✏️ Modifier
            </button>
            <button onClick={() => handleDeleteRevendeur(selectedRevendeur.id)} style={btnDanger}>
              🗑️ Supprimer
            </button>
          </div>
        </div>

        {/* Bannière rappel */}
        {prochainRappel && new Date(prochainRappel.date_rappel) <= new Date() && (
          <div style={{
            backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '10px',
            padding: '14px 20px', marginBottom: '20px', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontSize: '14px', color: '#856404' }}>
              📞 <strong>Rappel {new Date(prochainRappel.date_rappel) < new Date(new Date().toISOString().split('T')[0]) ? 'en retard' : "prévu aujourd'hui"}</strong> — {prochainRappel.contenu.substring(0, 80)}...
            </span>
            <button
              onClick={() => handleToggleRappel(prochainRappel)}
              style={{ ...btnPrimary, padding: '8px 16px', fontSize: '12px' }}
            >
              ✅ Marquer comme fait
            </button>
          </div>
        )}

        {/* Infos contact */}
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', fontSize: '14px', color: '#495057' }}>
            <div>
              <strong>Contact :</strong> {selectedRevendeur.prenom_contact} {selectedRevendeur.nom_contact}
            </div>
            <div><strong>📞</strong> {selectedRevendeur.telephone || '-'}</div>
            <div><strong>✉️</strong> {selectedRevendeur.email || '-'}</div>
            <div><strong>Type :</strong> {selectedRevendeur.types_revendeurs?.nom || '-'}</div>
            <div><strong>SIRET :</strong> {selectedRevendeur.siret || '-'}</div>
            <div><strong>TVA :</strong> {selectedRevendeur.tva_intracommunautaire || '-'}</div>
            <div><strong>🌐</strong> {selectedRevendeur.site_internet ? (
              <a href={selectedRevendeur.site_internet.startsWith('http') ? selectedRevendeur.site_internet : `https://${selectedRevendeur.site_internet}`} target="_blank" rel="noreferrer" style={{ color: '#1aabf9' }}>
                {selectedRevendeur.site_internet}
              </a>
            ) : '-'}</div>
            <div style={{ gridColumn: 'span 2' }}>
              <strong>📍</strong> {[selectedRevendeur.adresse, selectedRevendeur.code_postal, selectedRevendeur.ville, selectedRevendeur.code_pays].filter(Boolean).join(', ') || '-'}
            </div>
          </div>
        </div>

        {/* Cards stats */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: badge.color }}>{badge.icon} {(selectedRevendeur.credit_actuel || 0).toFixed(2)}€</div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
              Crédit actuel
              {selectedRevendeur.date_expiration_credit && (
                <span> • Expire le {formatDate(selectedRevendeur.date_expiration_credit)}</span>
              )}
            </div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#495057' }}>{nbMouvementsMois}</div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>Opérations ce mois</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#28a745' }}>+{totalCredite.toFixed(2)}€</div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>Total crédité</div>
            {totalBonus > 0 && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#6f42c1', marginTop: '6px' }}>+{totalBonus.toFixed(2)}€</div>
                <div style={{ fontSize: '11px', color: '#6c757d' }}>dont bonus offerts</div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#212529', marginTop: '6px', borderTop: '1px solid #e9ecef', paddingTop: '6px' }}>
                  = {totalCrediteCumule.toFixed(2)}€ total
                </div>
              </>
            )}
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#dc3545' }}>-{totalDebite.toFixed(2)}€</div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>Total débité</div>
          </div>
        </div>

        {/* Boutons actions */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          <button onClick={openModalCredit} style={{ ...btnPrimary, backgroundColor: '#28a745' }}>
            💳 Ajouter crédit
          </button>
          <button onClick={openModalDebit} style={{ ...btnPrimary, backgroundColor: '#e67e22' }}>
            📦 Nouvelle commande
          </button>
          <button onClick={openModalNote} style={{ ...btnPrimary, backgroundColor: '#6f42c1' }}>
            📝 Ajouter une note
          </button>
        </div>

        {/* Onglets Historique / Documents / Notes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
          {/* Historique mouvements */}
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#212529' }}>
              💰 Historique des mouvements ({mouvements.length})
            </h3>
            {mouvements.length === 0 ? (
              <p style={{ color: '#6c757d', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                Aucun mouvement enregistré
              </p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {mouvements.map(m => {
                  // Calculer le solde cumulé après ce mouvement
                  const isCredit = m.type === 'credit';
                  const bonusMvt = parseFloat(m.bonus_montant || 0);
                  const montantBase = isCredit && bonusMvt > 0 ? parseFloat(m.montant) - bonusMvt : parseFloat(m.montant);
                  return (
                    <div key={m.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px', borderBottom: '1px solid #f0f0f0', fontSize: '13px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#212529' }}>
                          {isCredit ? '💳' : '📦'} {m.reference || (isCredit ? 'Rechargement' : 'Commande')}
                          {m.email_envoye && (
                            <span title="Email envoyé" style={{ marginLeft: '6px', fontSize: '11px' }}>📧</span>
                          )}
                        </div>
                        <div style={{ color: '#6c757d', fontSize: '12px' }}>
                          {formatDate(m.date_document)}
                          {m.notes && ` • ${m.notes}`}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontWeight: '700', fontSize: '15px',
                          color: isCredit ? '#28a745' : '#dc3545'
                        }}>
                          {isCredit ? '+' : '-'}{parseFloat(m.montant).toFixed(2)}€
                        </div>
                        {isCredit && bonusMvt > 0 && (
                          <div style={{ fontSize: '11px', color: '#6f42c1', fontWeight: '600' }}>
                            dont 🎁 {bonusMvt.toFixed(2)}€ bonus
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents */}
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#212529' }}>
                📄 Documents ({documents.length})
              </h3>
              <button onClick={openModalDocument} title="Ajouter un document" style={{
                padding: '4px 10px', border: '1px solid #1aabf9', backgroundColor: '#e3f2fd',
                borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#1aabf9'
              }}>+ Ajouter</button>
            </div>
            {documents.length === 0 ? (
              <p style={{ color: '#6c757d', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                Aucun document enregistré
              </p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {documents.map(doc => (
                  <div key={doc.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '13px'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', color: '#212529', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {doc.type_document === 'facture' ? '🧾' : doc.type_document === 'bon_commande' ? '📦' : '📄'}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.nom_fichier}
                        </span>
                      </div>
                      <div style={{ color: '#6c757d', fontSize: '11px', marginTop: '2px' }}>
                        {formatDate(doc.created_at)} • {doc.taille_octets ? formatFileSize(doc.taille_octets) : ''}
                        {doc.type_document === 'facture' && <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', backgroundColor: '#e8f5e9', color: '#28a745', fontSize: '10px' }}>Facture</span>}
                        {doc.type_document === 'bon_commande' && <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', backgroundColor: '#fff3e0', color: '#e67e22', fontSize: '10px' }}>Bon de commande</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginLeft: '8px', flexShrink: 0 }}>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer"
                        title="Télécharger"
                        style={{
                          padding: '4px 8px', border: '1px solid #1aabf9', backgroundColor: '#e3f2fd',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '12px', textDecoration: 'none'
                        }}
                      >
                        ⬇️
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(doc)}
                        title="Supprimer"
                        style={{
                          padding: '4px 8px', border: '1px solid #dc3545', backgroundColor: '#fde8e8',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes commerciales */}
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#212529' }}>
              📝 Suivi commercial ({notesCommerciales.length})
              {dernierContact && (
                <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '400', marginLeft: '8px' }}>
                  Dernier contact : {formatDate(dernierContact)}
                </span>
              )}
            </h3>
            {notesCommerciales.length === 0 ? (
              <p style={{ color: '#6c757d', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                Aucune note commerciale
              </p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {notesCommerciales.map(note => {
                  const isRappelEnRetard = note.date_rappel && !note.rappel_effectue && new Date(note.date_rappel) <= new Date();
                  return (
                    <div key={note.id} style={{
                      padding: '12px', borderBottom: '1px solid #f0f0f0',
                      backgroundColor: isRappelEnRetard ? '#fff8e1' : 'transparent'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
                            📅 {formatDate(note.date_contact)}
                            {note.date_rappel && (
                              <span style={{
                                marginLeft: '10px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                                backgroundColor: note.rappel_effectue ? '#e8f5e9' : (isRappelEnRetard ? '#fde8e8' : '#e3f2fd'),
                                color: note.rappel_effectue ? '#28a745' : (isRappelEnRetard ? '#dc3545' : '#1976d2')
                              }}>
                                🔔 Rappel {formatDate(note.date_rappel)} {note.rappel_effectue ? '✅' : ''}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', color: '#212529', whiteSpace: 'pre-wrap' }}>
                            {note.contenu}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginLeft: '10px' }}>
                          {note.date_rappel && !note.rappel_effectue && (
                            <button
                              onClick={() => handleToggleRappel(note)}
                              title="Marquer rappel effectué"
                              style={{
                                padding: '4px 8px', border: '1px solid #28a745', backgroundColor: '#e8f5e9',
                                borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                              }}
                            >
                              ✅
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            title="Supprimer"
                            style={{
                              padding: '4px 8px', border: '1px solid #dc3545', backgroundColor: '#fde8e8',
                              borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ============================================ */}
        {/* MODALES */}
        {/* ============================================ */}

        {/* Modal Ajouter Crédit */}
        {showModalCredit && (
          <div style={modalOverlay}>
            <div style={{ ...modalContent, width: '450px' }}>
              <h3 style={{ margin: '0 0 24px 0', color: '#28a745' }}>💳 Ajouter un crédit</h3>
              <form onSubmit={handleAjouterCredit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Montant crédit (€) *</label>
                  <input type="number" step="0.01" min="0.01" required style={inputStyle}
                    value={formCredit.montant}
                    onChange={e => setFormCredit({ ...formCredit, montant: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>🎁 Montant bonus offert (€)</label>
                  <input type="number" step="0.01" min="0" style={inputStyle}
                    value={formCredit.bonus}
                    onChange={e => setFormCredit({ ...formCredit, bonus: e.target.value })}
                    placeholder="0.00 (optionnel)"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>N° de facture</label>
                  <input type="text" style={inputStyle}
                    value={formCredit.reference}
                    onChange={e => setFormCredit({ ...formCredit, reference: e.target.value })}
                    placeholder="FAC-2026-XXX"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Date de la facture</label>
                  <input type="date" style={inputStyle}
                    value={formCredit.date_document}
                    onChange={e => setFormCredit({ ...formCredit, date_document: e.target.value })}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                    value={formCredit.notes}
                    onChange={e => setFormCredit({ ...formCredit, notes: e.target.value })}
                  />
                </div>

                {/* Zone Drag & Drop PDF */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>📄 Joindre un document PDF (facture)</label>
                  {!pdfFile ? (
                    <div
                      onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#28a745'; e.currentTarget.style.backgroundColor = '#e8f5e9'; }}
                      onDragLeave={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                      onDrop={e => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        const file = e.dataTransfer.files[0];
                        if (file && file.type === 'application/pdf') {
                          if (file.size > 3 * 1024 * 1024) { alert('⚠️ Le fichier ne doit pas dépasser 3 Mo'); return; }
                          setPdfFile(file);
                        } else {
                          alert('⚠️ Seuls les fichiers PDF sont acceptés');
                        }
                      }}
                      onClick={() => document.getElementById('pdf-input-credit').click()}
                      style={{
                        border: '2px dashed #dee2e6', borderRadius: '10px', padding: '20px',
                        textAlign: 'center', cursor: 'pointer', backgroundColor: '#f8f9fa',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>📎</div>
                      <div style={{ fontSize: '13px', color: '#6c757d' }}>
                        Glissez-déposez un PDF ici ou <span style={{ color: '#28a745', textDecoration: 'underline' }}>cliquez pour parcourir</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '4px' }}>PDF uniquement • 3 Mo max</div>
                      <input id="pdf-input-credit" type="file" accept=".pdf" style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 3 * 1024 * 1024) { alert('⚠️ Le fichier ne doit pas dépasser 3 Mo'); return; }
                            setPdfFile(file);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', backgroundColor: '#e8f5e9', borderRadius: '10px',
                      border: '1px solid #28a745'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>📄</span>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>{pdfFile.name}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d' }}>{formatFileSize(pdfFile.size)}</div>
                        </div>
                      </div>
                      <button type="button" onClick={() => setPdfFile(null)} style={{
                        padding: '4px 10px', border: '1px solid #dc3545', backgroundColor: '#fde8e8',
                        borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#dc3545'
                      }}>✕</button>
                    </div>
                  )}
                </div>

                {/* Aperçu solde */}
                <div style={{
                  backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px',
                  marginBottom: '20px', fontSize: '14px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Crédit actuel</span>
                    <strong>{(selectedRevendeur.credit_actuel || 0).toFixed(2)}€</strong>
                  </div>
                  {(parseFloat(formCredit.montant) > 0) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#28a745' }}>
                      <span>+ Montant crédit</span>
                      <span>+{(parseFloat(formCredit.montant) || 0).toFixed(2)}€</span>
                    </div>
                  )}
                  {(parseFloat(formCredit.bonus) > 0) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#6f42c1' }}>
                      <span>+ 🎁 Bonus offert</span>
                      <span>+{(parseFloat(formCredit.bonus) || 0).toFixed(2)}€</span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', color: '#28a745', fontWeight: '700',
                    borderTop: '1px solid #dee2e6', paddingTop: '8px', marginTop: '4px'
                  }}>
                    <span>Nouveau solde</span>
                    <span>{((selectedRevendeur.credit_actuel || 0) + (parseFloat(formCredit.montant) || 0) + (parseFloat(formCredit.bonus) || 0)).toFixed(2)}€</span>
                  </div>
                </div>

                {/* Option envoi email */}
                {selectedRevendeur?.email && (
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    marginBottom: '20px', padding: '12px 16px',
                    backgroundColor: envoyerEmail ? '#e8f5e9' : '#f8f9fa',
                    borderRadius: '10px', cursor: 'pointer',
                    border: envoyerEmail ? '1px solid #28a745' : '1px solid #e9ecef',
                    transition: 'all 0.2s'
                  }}>
                    <input type="checkbox" checked={envoyerEmail}
                      onChange={e => setEnvoyerEmail(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: envoyerEmail ? '#28a745' : '#495057' }}>
                        📧 Envoyer un récapitulatif par email
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                        → {selectedRevendeur.email}
                      </div>
                    </div>
                  </label>
                )}
                {!selectedRevendeur?.email && (
                  <div style={{
                    marginBottom: '20px', padding: '10px 14px',
                    backgroundColor: '#fff3cd', borderRadius: '8px',
                    fontSize: '12px', color: '#856404'
                  }}>
                    ⚠️ Pas d'email renseigné — ajoutez un email au revendeur pour activer l'envoi
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowModalCredit(false)} style={btnSecondary}>Annuler</button>
                  <button type="submit" style={{ ...btnPrimary, backgroundColor: '#28a745' }} disabled={envoiEnCours}>
                    {envoiEnCours ? '⏳ Envoi...' : '✅ Créditer le compte'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Nouvelle Commande (débit) */}
        {showModalDebit && (
          <div style={modalOverlay}>
            <div style={{ ...modalContent, width: '450px' }}>
              <h3 style={{ margin: '0 0 24px 0', color: '#e67e22' }}>📦 Nouvelle commande</h3>
              <form onSubmit={handleAjouterDebit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>N° de bon de commande</label>
                  <input type="text" style={inputStyle}
                    value={formDebit.reference}
                    onChange={e => setFormDebit({ ...formDebit, reference: e.target.value })}
                    placeholder="BC-2026-XXX"
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Date du bon de commande</label>
                  <input type="date" style={inputStyle}
                    value={formDebit.date_document}
                    onChange={e => setFormDebit({ ...formDebit, date_document: e.target.value })}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Montant total à débiter (€) *</label>
                  <input type="number" step="0.01" min="0.01" required style={inputStyle}
                    value={formDebit.montant}
                    onChange={e => setFormDebit({ ...formDebit, montant: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                    value={formDebit.notes}
                    onChange={e => setFormDebit({ ...formDebit, notes: e.target.value })}
                  />
                </div>

                {/* Zone Drag & Drop PDF */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>📄 Joindre un document PDF (bon de commande)</label>
                  {!pdfFile ? (
                    <div
                      onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#e67e22'; e.currentTarget.style.backgroundColor = '#fff3e0'; }}
                      onDragLeave={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                      onDrop={e => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        const file = e.dataTransfer.files[0];
                        if (file && file.type === 'application/pdf') {
                          if (file.size > 3 * 1024 * 1024) { alert('⚠️ Le fichier ne doit pas dépasser 3 Mo'); return; }
                          setPdfFile(file);
                        } else {
                          alert('⚠️ Seuls les fichiers PDF sont acceptés');
                        }
                      }}
                      onClick={() => document.getElementById('pdf-input-debit').click()}
                      style={{
                        border: '2px dashed #dee2e6', borderRadius: '10px', padding: '20px',
                        textAlign: 'center', cursor: 'pointer', backgroundColor: '#f8f9fa',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontSize: '24px', marginBottom: '6px' }}>📎</div>
                      <div style={{ fontSize: '13px', color: '#6c757d' }}>
                        Glissez-déposez un PDF ici ou <span style={{ color: '#e67e22', textDecoration: 'underline' }}>cliquez pour parcourir</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '4px' }}>PDF uniquement • 3 Mo max</div>
                      <input id="pdf-input-debit" type="file" accept=".pdf" style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 3 * 1024 * 1024) { alert('⚠️ Le fichier ne doit pas dépasser 3 Mo'); return; }
                            setPdfFile(file);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', backgroundColor: '#fff3e0', borderRadius: '10px',
                      border: '1px solid #e67e22'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>📄</span>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>{pdfFile.name}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d' }}>{formatFileSize(pdfFile.size)}</div>
                        </div>
                      </div>
                      <button type="button" onClick={() => setPdfFile(null)} style={{
                        padding: '4px 10px', border: '1px solid #dc3545', backgroundColor: '#fde8e8',
                        borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#dc3545'
                      }}>✕</button>
                    </div>
                  )}
                </div>

                {/* Aperçu solde */}
                <div style={{
                  backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px',
                  marginBottom: '20px', fontSize: '14px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Crédit actuel</span>
                    <strong>{(selectedRevendeur.credit_actuel || 0).toFixed(2)}€</strong>
                  </div>
                  {formDebit.montant && (
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', fontWeight: '700',
                      color: ((selectedRevendeur.credit_actuel || 0) - (parseFloat(formDebit.montant) || 0)) < 0 ? '#dc3545' : '#e67e22'
                    }}>
                      <span>Après commande</span>
                      <span>{((selectedRevendeur.credit_actuel || 0) - (parseFloat(formDebit.montant) || 0)).toFixed(2)}€</span>
                    </div>
                  )}
                </div>

                {/* Option envoi email */}
                {selectedRevendeur?.email && (
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    marginBottom: '20px', padding: '12px 16px',
                    backgroundColor: envoyerEmail ? '#fff3e0' : '#f8f9fa',
                    borderRadius: '10px', cursor: 'pointer',
                    border: envoyerEmail ? '1px solid #e67e22' : '1px solid #e9ecef',
                    transition: 'all 0.2s'
                  }}>
                    <input type="checkbox" checked={envoyerEmail}
                      onChange={e => setEnvoyerEmail(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: envoyerEmail ? '#e67e22' : '#495057' }}>
                        📧 Envoyer un récapitulatif par email
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                        → {selectedRevendeur.email}
                      </div>
                    </div>
                  </label>
                )}
                {!selectedRevendeur?.email && (
                  <div style={{
                    marginBottom: '20px', padding: '10px 14px',
                    backgroundColor: '#fff3cd', borderRadius: '8px',
                    fontSize: '12px', color: '#856404'
                  }}>
                    ⚠️ Pas d'email renseigné — ajoutez un email au revendeur pour activer l'envoi
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowModalDebit(false)} style={btnSecondary}>Annuler</button>
                  <button type="submit" style={{ ...btnPrimary, backgroundColor: '#e67e22' }} disabled={envoiEnCours}>
                    {envoiEnCours ? '⏳ Envoi...' : '✅ Valider & Débiter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Note Commerciale */}
        {showModalNote && (
          <div style={modalOverlay}>
            <div style={{ ...modalContent, width: '500px' }}>
              <h3 style={{ margin: '0 0 24px 0', color: '#6f42c1' }}>📝 Nouvelle note commerciale</h3>
              <form onSubmit={handleAjouterNote}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Date du contact *</label>
                  <input type="date" required style={inputStyle}
                    value={formNote.date_contact}
                    onChange={e => setFormNote({ ...formNote, date_contact: e.target.value })}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Résumé de l'échange *</label>
                  <textarea required style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                    value={formNote.contenu}
                    onChange={e => setFormNote({ ...formNote, contenu: e.target.value })}
                    placeholder="Résumé de l'appel, échange email, visite..."
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>🔔 Date de rappel (optionnel)</label>
                  <input type="date" style={inputStyle}
                    value={formNote.date_rappel}
                    onChange={e => setFormNote({ ...formNote, date_rappel: e.target.value })}
                  />
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                    Vous recevrez une notification à cette date pour penser à rappeler ce revendeur
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowModalNote(false)} style={btnSecondary}>Annuler</button>
                  <button type="submit" style={{ ...btnPrimary, backgroundColor: '#6f42c1' }}>✅ Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Revendeur (modification depuis la fiche) */}
        {showModalRevendeur && renderModalRevendeur()}

        {/* Modal Ajout Document Manuel */}
        {showModalDocument && (
          <div style={modalOverlay}>
            <div style={{ ...modalContent, width: '450px' }}>
              <h3 style={{ margin: '0 0 24px 0', color: '#1aabf9' }}>📄 Ajouter un document</h3>
              <form onSubmit={handleAjouterDocumentManuel}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Type de document</label>
                  <select style={inputStyle} value={formDocType} onChange={e => setFormDocType(e.target.value)}>
                    <option value="facture">🧾 Facture</option>
                    <option value="bon_commande">📦 Bon de commande</option>
                    <option value="autre">📄 Autre</option>
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Fichier PDF *</label>
                  {!pdfFile ? (
                    <div
                      onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#1aabf9'; e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                      onDragLeave={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                      onDrop={e => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        const file = e.dataTransfer.files[0];
                        if (file && file.type === 'application/pdf') {
                          if (file.size > 3 * 1024 * 1024) { alert('⚠️ Le fichier ne doit pas dépasser 3 Mo'); return; }
                          setPdfFile(file);
                        } else {
                          alert('⚠️ Seuls les fichiers PDF sont acceptés');
                        }
                      }}
                      onClick={() => document.getElementById('pdf-input-manual').click()}
                      style={{
                        border: '2px dashed #dee2e6', borderRadius: '10px', padding: '24px',
                        textAlign: 'center', cursor: 'pointer', backgroundColor: '#f8f9fa',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontSize: '28px', marginBottom: '8px' }}>📎</div>
                      <div style={{ fontSize: '13px', color: '#6c757d' }}>
                        Glissez-déposez un PDF ici ou <span style={{ color: '#1aabf9', textDecoration: 'underline' }}>cliquez pour parcourir</span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '4px' }}>PDF uniquement • 3 Mo max</div>
                      <input id="pdf-input-manual" type="file" accept=".pdf" style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 3 * 1024 * 1024) { alert('⚠️ Le fichier ne doit pas dépasser 3 Mo'); return; }
                            setPdfFile(file);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', backgroundColor: '#e3f2fd', borderRadius: '10px',
                      border: '1px solid #1aabf9'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>📄</span>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>{pdfFile.name}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d' }}>{formatFileSize(pdfFile.size)}</div>
                        </div>
                      </div>
                      <button type="button" onClick={() => setPdfFile(null)} style={{
                        padding: '4px 10px', border: '1px solid #dc3545', backgroundColor: '#fde8e8',
                        borderRadius: '6px', cursor: 'pointer', fontSize: '13px', color: '#dc3545'
                      }}>✕</button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => { setShowModalDocument(false); setPdfFile(null); }} style={btnSecondary}>Annuler</button>
                  <button type="submit" disabled={!pdfFile} style={{ ...btnPrimary, backgroundColor: '#1aabf9', opacity: pdfFile ? 1 : 0.5 }}>
                    ✅ Enregistrer le document
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==============================================
  // RENDER MODAL REVENDEUR (partagé entre liste et fiche)
  // ==============================================
  function renderModalRevendeur() {
    return (
      <div style={modalOverlay}>
        <div style={{ ...modalContent, width: '700px' }}>
          <h3 style={{ margin: '0 0 24px 0', color: '#212529' }}>
            {editingRevendeur ? '✏️ Modifier le revendeur' : '🏪 Nouveau revendeur'}
          </h3>
          <form onSubmit={handleSaveRevendeur}>
            {/* Nom boutique */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Nom de la boutique *</label>
              <input type="text" required style={inputStyle}
                value={formRevendeur.nom_boutique}
                onChange={e => setFormRevendeur({ ...formRevendeur, nom_boutique: e.target.value })}
              />
            </div>

            {/* Contact */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Prénom contact</label>
                <input type="text" style={inputStyle}
                  value={formRevendeur.prenom_contact}
                  onChange={e => setFormRevendeur({ ...formRevendeur, prenom_contact: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Nom contact</label>
                <input type="text" style={inputStyle}
                  value={formRevendeur.nom_contact}
                  onChange={e => setFormRevendeur({ ...formRevendeur, nom_contact: e.target.value })}
                />
              </div>
            </div>

            {/* Type + Téléphone */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>
                  Type de revendeur
                  <button type="button" onClick={() => setShowModalType(true)}
                    style={{ marginLeft: '8px', fontSize: '11px', color: '#1aabf9', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Gérer les types
                  </button>
                </label>
                <select style={inputStyle}
                  value={formRevendeur.type_revendeur_id}
                  onChange={e => setFormRevendeur({ ...formRevendeur, type_revendeur_id: e.target.value })}
                >
                  <option value="">-- Sélectionner --</option>
                  {typesRevendeurs.map(t => (
                    <option key={t.id} value={t.id}>{t.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Téléphone</label>
                <input type="tel" style={inputStyle}
                  value={formRevendeur.telephone}
                  onChange={e => setFormRevendeur({ ...formRevendeur, telephone: e.target.value })}
                />
              </div>
            </div>

            {/* Email + Site */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" style={inputStyle}
                  value={formRevendeur.email}
                  onChange={e => setFormRevendeur({ ...formRevendeur, email: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Site internet</label>
                <input type="text" style={inputStyle}
                  value={formRevendeur.site_internet}
                  onChange={e => setFormRevendeur({ ...formRevendeur, site_internet: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* SIRET + TVA */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>N° SIRET</label>
                <input type="text" style={inputStyle}
                  value={formRevendeur.siret}
                  onChange={e => setFormRevendeur({ ...formRevendeur, siret: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>N° TVA intracommunautaire</label>
                <input type="text" style={inputStyle}
                  value={formRevendeur.tva_intracommunautaire}
                  onChange={e => setFormRevendeur({ ...formRevendeur, tva_intracommunautaire: e.target.value })}
                />
              </div>
            </div>

            {/* Adresse */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Adresse</label>
              <input type="text" style={inputStyle}
                value={formRevendeur.adresse}
                onChange={e => setFormRevendeur({ ...formRevendeur, adresse: e.target.value })}
              />
            </div>

            {/* CP + Ville + Pays */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '12px', marginBottom: '24px' }}>
              <div>
                <label style={labelStyle}>Code postal</label>
                <input type="text" style={inputStyle}
                  value={formRevendeur.code_postal}
                  onChange={e => setFormRevendeur({ ...formRevendeur, code_postal: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Ville</label>
                <input type="text" style={inputStyle}
                  value={formRevendeur.ville}
                  onChange={e => setFormRevendeur({ ...formRevendeur, ville: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Code pays</label>
                <input type="text" style={inputStyle} maxLength={5}
                  value={formRevendeur.code_pays}
                  onChange={e => setFormRevendeur({ ...formRevendeur, code_pays: e.target.value.toUpperCase() })}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowModalRevendeur(false)} style={btnSecondary}>Annuler</button>
              <button type="submit" style={btnPrimary}>
                {editingRevendeur ? '✅ Enregistrer' : '✅ Créer le revendeur'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ==============================================
  // VUE LISTE DES REVENDEURS
  // ==============================================
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', color: '#212529' }}>
          🏪 Gestion des revendeurs
        </h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {rappelsEnRetard.length > 0 && (
            <span style={{
              padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600',
              backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107'
            }}>
              🔔 {rappelsEnRetard.length} rappel{rappelsEnRetard.length > 1 ? 's' : ''} en attente
            </span>
          )}
          <button onClick={() => openModalRevendeur()} style={btnPrimary}>
            + Nouveau revendeur
          </button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="🔍 Rechercher un revendeur (nom, contact, email, ville...)"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ ...inputStyle, width: '400px', maxWidth: '100%' }}
        />
      </div>

      {/* Rappels en retard */}
      {rappelsEnRetard.length > 0 && (
        <div style={{
          backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '10px',
          padding: '16px', marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#856404', fontSize: '14px' }}>
            📞 Rappels en attente
          </h4>
          {rappelsEnRetard.map(r => (
            <div key={r.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderBottom: '1px solid #ffe69c', fontSize: '13px'
            }}>
              <span>
                <strong>{r.revendeurs?.nom_boutique}</strong> — {r.contenu.substring(0, 60)}...
                <span style={{ color: '#856404', marginLeft: '8px' }}>({formatDate(r.date_rappel)})</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Tableau revendeurs */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e5ea' }}>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Boutique</th>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Contact</th>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Type</th>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Ville</th>
              <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Crédit</th>
              <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Expiration</th>
              <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {revendeursFiltres.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
                  {searchTerm ? 'Aucun revendeur ne correspond à la recherche' : 'Aucun revendeur enregistré'}
                </td>
              </tr>
            ) : (
              revendeursFiltres.map(r => {
                const badge = getCreditBadge(r);
                const creditNegatif = (r.credit_actuel || 0) < 0;
                return (
                  <tr key={r.id} style={{
                    borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    backgroundColor: creditNegatif ? '#fde8e8' : 'transparent'
                  }}
                    onClick={() => handleSelectRevendeur(r)}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = creditNegatif ? '#f8d7da' : '#f8f9fa'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = creditNegatif ? '#fde8e8' : 'transparent'}
                  >
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#212529' }}>
                      {r.nom_boutique}
                      {!r.actif && <span style={{ color: '#dc3545', fontSize: '11px', marginLeft: '8px' }}>(inactif)</span>}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#495057' }}>
                      {[r.prenom_contact, r.nom_contact].filter(Boolean).join(' ') || '-'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6c757d' }}>
                      {r.types_revendeurs?.nom || '-'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6c757d' }}>
                      {r.ville || '-'}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '700',
                        backgroundColor: badge.bg, color: badge.color
                      }}>
                        {badge.icon} {(r.credit_actuel || 0).toFixed(2)}€
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '12px', color: '#6c757d' }}>
                      {r.date_expiration_credit ? formatDate(r.date_expiration_credit) : '-'}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleSelectRevendeur(r)}
                          title="Voir la fiche"
                          style={{
                            padding: '6px 10px', border: '1px solid #dee2e6', backgroundColor: '#f8f9fa',
                            borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
                          }}
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => openModalRevendeur(r)}
                          title="Modifier"
                          style={{
                            padding: '6px 10px', border: '1px solid #dee2e6', backgroundColor: '#f8f9fa',
                            borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteRevendeur(r.id)}
                          title="Supprimer"
                          style={{
                            padding: '6px 10px', border: '1px solid #fde8e8', backgroundColor: '#fde8e8',
                            borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal création/édition revendeur */}
      {showModalRevendeur && renderModalRevendeur()}

      {/* Modal gestion types revendeurs */}
      {showModalType && (
        <div style={modalOverlay}>
          <div style={{ ...modalContent, width: '400px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#212529' }}>📋 Types de revendeurs</h3>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <input type="text" style={{ ...inputStyle, flex: 1 }}
                value={newTypeName}
                onChange={e => setNewTypeName(e.target.value)}
                placeholder="Nouveau type..."
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAjouterType())}
              />
              <button onClick={handleAjouterType} style={{ ...btnPrimary, padding: '10px 16px' }}>+</button>
            </div>

            {typesRevendeurs.map(t => (
              <div key={t.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px', borderBottom: '1px solid #f0f0f0'
              }}>
                <span style={{ fontSize: '14px' }}>{t.nom}</span>
                <button onClick={() => handleDeleteType(t.id)} style={{
                  padding: '4px 8px', border: '1px solid #fde8e8', backgroundColor: '#fde8e8',
                  borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                }}>🗑️</button>
              </div>
            ))}

            <div style={{ textAlign: 'right', marginTop: '20px' }}>
              <button onClick={() => setShowModalType(false)} style={btnSecondary}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionRevendeurs;
